import React, { useState, useEffect } from "react";
import { Table, Spin, Tag, Space, Typography, Select, Row, Col, theme } from "antd";
import {
  LoadingOutlined,
  UserOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  BarsOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { PageLayout, PageHeader, SectionCard, TableToolbar } from "../../Common/Layout";
import BiometricStatsService, { IBiometricListItem } from "../../Services/BiometricStats";
import { IPractices, usePractice } from "../../Context/PracticeContext";

const { Text } = Typography;

interface BiometricStatusMeta {
  label: string;
  color: string;
}

const STATUS_MAP: Record<number, BiometricStatusMeta> = {
  1: { label: "Requested", color: "blue" },
  2: { label: "Completed", color: "green" },
  3: { label: "Failed", color: "red" },
  4: { label: "Rejected", color: "orange" },
};

const STATUS_CARDS = [
  {
    status: 1 as number | undefined,
    label: "Requested",
    icon: <ClockCircleOutlined />,
    bgLight: "#e6f0ff",
    bgDark: "#1D3461",
    fg: "#1890ff",
  },
  {
    status: 2 as number | undefined,
    label: "Completed",
    icon: <CheckCircleOutlined />,
    bgLight: "#e9f9ee",
    bgDark: "#1B3B22",
    fg: "#52c41a",
  },
  {
    status: 3 as number | undefined,
    label: "Failed",
    icon: <CloseCircleOutlined />,
    bgLight: "#fff1f0",
    bgDark: "#3F1B1B",
    fg: "#ff4d4f",
  },
  {
    status: 4 as number | undefined,
    label: "Rejected",
    icon: <MinusCircleOutlined />,
    bgLight: "#fff7e6",
    bgDark: "#2b2111",
    fg: "#fa8c16",
  },
  {
    status: undefined as number | undefined,
    label: "Total",
    icon: <BarsOutlined />,
    bgLight: "#f9f0ff",
    bgDark: "#2b1d3d",
    fg: "#722ed1",
  },
];

const getStatusFilterOptions = () =>
  Object.entries(STATUS_MAP).map(([value, meta]) => ({ value, label: meta.label }));

const formatDate = (value?: string | null): string => {
  if (!value) return "-";
  return dayjs(value).format("DD/MM/YYYY");
};

const defaultDateRange: [Dayjs, Dayjs] = [dayjs().startOf("month"), dayjs().endOf("month")];

const isDefaultDateRange = (value?: [Dayjs | null, Dayjs | null]) => {
  if (!value?.[0] || !value?.[1]) return true;
  return (
    value[0].isSame(defaultDateRange[0], "day") &&
    value[1].isSame(defaultDateRange[1], "day")
  );
};

const extractTotalRecords = (res: any): number => {
  const payload = res?.result ?? res;
  const value = payload?.totalRecords ?? payload?.TotalRecords ?? 0;
  return Number(value) || 0;
};

interface BiometricListState {
  items: IBiometricListItem[];
  totalItems: number;
}

interface IBiometricStatusCounts {
  requested: number;
  completed: number;
  failed: number;
  rejected: number;
  total: number;
}

const BiometricReports: React.FC = () => {
  const { token } = theme.useToken();
  const isDark = token.colorTextBase === "#E9EEF9";
  const { allPracticeIds } = usePractice();
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [dataList, setDataList] = useState<BiometricListState>({
    items: [],
    totalItems: 0,
  });
  const [statusCounts, setStatusCounts] = useState<IBiometricStatusCounts>({
    requested: 0,
    completed: 0,
    failed: 0,
    rejected: 0,
    total: 0,
  });

  const [listParams, setListParams] = useState<{
    practiceId?: number;
    status?: number;
    search: string;
    dateRange: [Dayjs, Dayjs];
    sortDir: string;
    sortCol: string;
    currentPage: number;
    pageSize: number;
  }>({
    practiceId: undefined,
    status: undefined,
    search: "",
    dateRange: defaultDateRange,
    sortDir: "descend",
    sortCol: "RequestedOn",
    currentPage: 1,
    pageSize: 15,
  });

  useEffect(() => {
    if (allPracticeIds?.length > 0) {
      setListParams((prev) => ({
        ...prev,
        practiceId: prev.practiceId ?? allPracticeIds[0].value,
      }));
    }
  }, [allPracticeIds]);

  const fetchItems = async (start: number, length: number) => {
    if (!listParams.practiceId) return;

    setLoading(true);
    try {
      const fromDate = isDefaultDateRange(listParams.dateRange)
        ? listParams.dateRange[0].format("DD-MM-YYYY")
        : listParams.dateRange[0]?.format("DD-MM-YYYY");
      const toDate = isDefaultDateRange(listParams.dateRange)
        ? listParams.dateRange[1].format("DD-MM-YYYY")
        : listParams.dateRange[1]?.format("DD-MM-YYYY");

      const response = await BiometricStatsService.getBiometricList(
        listParams.practiceId,
        listParams.status,
        start,
        length,
        listParams.search,
        fromDate,
        toDate,
        listParams.sortCol,
        listParams.sortDir === "ascend" ? "ascend" : "descend"
      );

      const result = response?.result ?? response;
      setDataList({
        items: Array.isArray(result?.items) ? result.items : [],
        totalItems: extractTotalRecords(response),
      });
    } catch (error) {
      console.error("Failed to load biometric list", error);
      setDataList({ items: [], totalItems: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(
      (listParams.currentPage - 1) * listParams.pageSize,
      listParams.pageSize
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listParams, refresh]);

  useEffect(() => {
    const loadStatusCounts = async () => {
      if (!listParams.practiceId) return;

      const fromDate = listParams.dateRange[0]?.format("DD-MM-YYYY");
      const toDate = listParams.dateRange[1]?.format("DD-MM-YYYY");

      try {
        const [requestedRes, completedRes, failedRes, rejectedRes, totalRes] = await Promise.all([
          BiometricStatsService.getBiometricList(
            listParams.practiceId,
            1,
            0,
            1,
            listParams.search,
            fromDate,
            toDate,
            listParams.sortCol,
            listParams.sortDir === "ascend" ? "ascend" : "descend"
          ),
          BiometricStatsService.getBiometricList(
            listParams.practiceId,
            2,
            0,
            1,
            listParams.search,
            fromDate,
            toDate,
            listParams.sortCol,
            listParams.sortDir === "ascend" ? "ascend" : "descend"
          ),
          BiometricStatsService.getBiometricList(
            listParams.practiceId,
            3,
            0,
            1,
            listParams.search,
            fromDate,
            toDate,
            listParams.sortCol,
            listParams.sortDir === "ascend" ? "ascend" : "descend"
          ),
          BiometricStatsService.getBiometricList(
            listParams.practiceId,
            4,
            0,
            1,
            listParams.search,
            fromDate,
            toDate,
            listParams.sortCol,
            listParams.sortDir === "ascend" ? "ascend" : "descend"
          ),
          BiometricStatsService.getBiometricList(
            listParams.practiceId,
            undefined,
            0,
            1,
            listParams.search,
            fromDate,
            toDate,
            listParams.sortCol,
            listParams.sortDir === "ascend" ? "ascend" : "descend"
          ),
        ]);

        setStatusCounts({
          requested: extractTotalRecords(requestedRes),
          completed: extractTotalRecords(completedRes),
          failed: extractTotalRecords(failedRes),
          rejected: extractTotalRecords(rejectedRes),
          total: extractTotalRecords(totalRes),
        });
      } catch (error) {
        console.error("Failed to load biometric status counts", error);
      }
    };

    loadStatusCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listParams.practiceId, listParams.search, listParams.dateRange, refresh]);

  const handleSearch = (value: string) => {
    setListParams((prev) => ({ ...prev, search: value, currentPage: 1 }));
  };

  const handleRefresh = () => {
    setListParams((prev) => ({
      ...prev,
      search: "",
      status: undefined,
      dateRange: defaultDateRange,
      currentPage: 1,
      pageSize: 15,
    }));
    setRefresh((r) => !r);
  };

  const handleStatusCardClick = (status?: number) => {
    setListParams((prev) => ({ ...prev, status, currentPage: 1 }));
  };

  const handlePracticeChange = (value?: number) => {
    setListParams((prev) => ({ ...prev, practiceId: value, currentPage: 1 }));
  };


  const filterCriteria = [
    {
      key: "status",
      label: "Biometric Status",
      type: "select" as const,
      options: [{ value: "", label: "All" }, ...getStatusFilterOptions()],
      placeholder: "Select Status",
    },
    {
      key: "dateRange",
      label: "Date",
      type: "dateRange" as const,
    },
  ];

  const activeFilters = {
    status: listParams.status !== undefined ? String(listParams.status) : "",
    dateRange: listParams.dateRange,
  };

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    const normalizeStatus = (value: any) => {
      if (value === undefined || value === null || value === "") return undefined;
      return Number(value);
    };

    setListParams((prev) => ({
      ...prev,
      status: normalizeStatus(newFilters.status),
      dateRange: newFilters.dateRange ?? [dayjs().startOf("month"), dayjs().endOf("month")],
      currentPage: 1,
    }));
  };

  const columns: ColumnsType<IBiometricListItem> = [
    {
      title: "S.No.",
      key: "sno",
      width: 60,
      render: (_: unknown, __: IBiometricListItem, index: number) =>
        listParams.currentPage > 1
          ? listParams.pageSize * (listParams.currentPage - 1) + index + 1
          : index + 1,
    },
    {
      title: "Member Name",
      dataIndex: "memberName",
      key: "MemberName",
      width: "22%",
      sorter: true,
      render: (name: string) => (
        <Space>
          <UserOutlined style={{ color: "#1890ff" }} />
          <Text strong>{name || "N/A"}</Text>
        </Space>
      ),
    },
    {
      title: "Business Name",
      dataIndex: "businessName",
      key: "BusinessName",
      width: "22%",
      sorter: true,
      render: (name: string) => (
        <Space>
          <ShopOutlined style={{ color: "#52c41a" }} />
          <span>{name || "N/A"}</span>
        </Space>
      ),
    },
    {
      title: "Biometric Status",
      dataIndex: "status",
      key: "Status",
      width: "15%",
      render: (status: number) => {
        const meta = STATUS_MAP[status] ?? { label: "Unknown", color: "default" };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: "Biometric Requested On",
      dataIndex: "requestedOn",
      key: "RequestedOn",
      width: "18%",
      sorter: true,
      render: (val: string) => <span>{formatDate(val)}</span>,
    },
    {
      title: "Biometric Completed On",
      dataIndex: "completedOn",
      key: "CompletedOn",
      width: "18%",
      render: (val: string | null) => <span>{formatDate(val)}</span>,
    },
  ];

  return (
    <PageLayout>
      <PageHeader title="Biometric" subtitle="Biometric verification status for company members" />

      <Row gutter={[10, 10]} style={{ marginBottom: 16 }}>
        {STATUS_CARDS.map((card) => {
          const isActive = listParams.status === card.status;
          const bg = isDark ? card.bgDark : card.bgLight;
          const count =
            card.status === 1
              ? statusCounts.requested
              : card.status === 2
                ? statusCounts.completed
                : card.status === 3
                  ? statusCounts.failed
                  : card.status === 4
                    ? statusCounts.rejected
                    : statusCounts.total;

          return (
            <Col key={card.label} xs={12} sm={8} md={6} lg={4} xl={3}>
              <div
                onClick={() => handleStatusCardClick(card.status)}
                style={{
                  background: bg,
                  border: `1.5px solid ${isActive ? card.fg : card.fg + "30"}`,
                  borderRadius: 10,
                  padding: "10px 14px",
                  cursor: "pointer",
                  transition: "transform 0.15s, box-shadow 0.15s",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  boxShadow: isActive ? `0 0 0 2px ${card.fg}40` : "none",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px)";
                  (e.currentTarget as HTMLDivElement).style.boxShadow = `0 4px 12px ${card.fg}30`;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
                  if (!isActive) (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: `${card.fg}20`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: card.fg,
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: isDark ? "#9CA3AF" : "#6B7280",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {card.label}
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: card.fg,
                      lineHeight: 1.2,
                    }}
                  >
                    {count.toLocaleString()}
                  </div>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>

      <SectionCard noPadding>
        <div style={{ padding: "0 16px" }}>
          <TableToolbar
            onSearch={handleSearch}
            searchPlaceholder="Search member or business name..."
            filterCriteria={filterCriteria}
            activeFilters={activeFilters}
            onFiltersChange={handleFiltersChange}
            onRefresh={handleRefresh}
            extra={
              <Select
                placeholder="Select practice"
                style={{ width: 220 }}
                value={listParams.practiceId}
                onChange={handlePracticeChange}
                options={allPracticeIds.map((p: IPractices) => ({
                  value: p.value,
                  label: p.label + " (ID: " + p.value + ")",
                }))}
              />
            }
          />
        </div>
        <Table
          dataSource={dataList.items}
          columns={columns}
          rowKey="id"
          size="small"
          scroll={{ x: 1000, y: window.innerHeight - 270 }}
          loading={{
            spinning: loading,
            indicator: <Spin indicator={<LoadingOutlined />} />,
          }}
          className="border-top-table"
          pagination={{
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
            current: listParams.currentPage,
            pageSize: listParams.pageSize,
            showSizeChanger: true,
            total: dataList.totalItems,
            pageSizeOptions: ["15", "25", "50", "100"],
          }}
          onChange={(paging, _filter, sorter) => {
            if (paging?.current === undefined || paging?.pageSize === undefined) return;

            const current = paging.current;
            const pageSize = paging.pageSize;
            const sort = Array.isArray(sorter) ? sorter[0] : sorter;

            setListParams((prev) => ({
              ...prev,
              sortDir: sort?.order ? (sort.order as string) : "descend",
              sortCol: sort?.order ? String(sort.columnKey) : "RequestedOn",
              currentPage: current,
              pageSize: pageSize,
            }));
          }}
        />
      </SectionCard>
    </PageLayout>
  );
};

export default BiometricReports;